import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, Timestamp, deleteField, writeBatch
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Service } from '../../types';

const SERVICES_COLLECTION = 'services';

const toTimestampOrNull = (date: Date | null | undefined): Timestamp | null => {
  if (!date) return null;
  return Timestamp.fromDate(date instanceof Date ? date : new Date(date));
};

const toDateOrNull = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  return new Date(timestamp);
};

export const servicesService = {
  async getServices(): Promise<Service[]> {
    const q = query(collection(db, SERVICES_COLLECTION));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || '',
        url: data.url || '',
        username: data.username || '',
        password: data.password || '',
        description: data.description || '',
        category: data.category || '',
        office: data.office || 'shared',
        cost: data.cost ?? null,
        currency: data.currency || 'NOK',
        billingCycle: data.billingCycle || 'monthly',
        renewalDate: toDateOrNull(data.renewalDate),
        tags: data.tags || [],
        notes: data.notes || '',
        claimed: data.claimed || false,
        claimedBy: data.claimedBy,
        claimedDate: toDateOrNull(data.claimedDate),
        deleted: data.deleted || false,
        deletedBy: data.deletedBy,
        deletedDate: toDateOrNull(data.deletedDate),
        permanentlyDeleted: data.permanentlyDeleted,
        permanentlyDeletedDate: toDateOrNull(data.permanentlyDeletedDate),
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
        createdBy: data.createdBy || '',
      } as Service;
    });
  },

  async addService(data: Omit<Service, 'id' | 'createdAt'>): Promise<string> {
    const docData: any = {
      ...data,
      createdAt: Timestamp.now(),
      renewalDate: toTimestampOrNull(data.renewalDate),
      claimedDate: toTimestampOrNull(data.claimedDate),
      deletedDate: toTimestampOrNull(data.deletedDate),
    };
    const docRef = await addDoc(collection(db, SERVICES_COLLECTION), docData);
    return docRef.id;
  },

  async updateService(id: string, updates: Partial<Service>): Promise<void> {
    const docRef = doc(db, SERVICES_COLLECTION, id);
    const updateData: any = { ...updates };

    // Convert date fields to Timestamps
    if ('renewalDate' in updates) {
      updateData.renewalDate = toTimestampOrNull(updates.renewalDate);
    }
    if ('claimedDate' in updates) {
      updateData.claimedDate = toTimestampOrNull(updates.claimedDate);
    }
    if ('deletedDate' in updates) {
      updateData.deletedDate = toTimestampOrNull(updates.deletedDate);
    }
    if ('permanentlyDeletedDate' in updates) {
      updateData.permanentlyDeletedDate = toTimestampOrNull(updates.permanentlyDeletedDate);
    }

    // Remove id from updates if present
    delete updateData.id;

    await updateDoc(docRef, updateData);
  },

  async claimService(id: string, email: string): Promise<void> {
    const docRef = doc(db, SERVICES_COLLECTION, id);
    await updateDoc(docRef, {
      claimed: true,
      claimedBy: email,
      claimedDate: Timestamp.now(),
    });
  },

  async releaseService(id: string): Promise<void> {
    const docRef = doc(db, SERVICES_COLLECTION, id);
    await updateDoc(docRef, {
      claimed: false,
      claimedBy: deleteField(),
      claimedDate: deleteField(),
    });
  },

  async deleteService(id: string, email: string): Promise<void> {
    const docRef = doc(db, SERVICES_COLLECTION, id);
    await updateDoc(docRef, {
      deleted: true,
      deletedBy: email,
      deletedDate: Timestamp.now(),
    });
  },

  async restoreService(id: string): Promise<void> {
    const docRef = doc(db, SERVICES_COLLECTION, id);
    await updateDoc(docRef, {
      deleted: false,
      deletedBy: deleteField(),
      deletedDate: deleteField(),
    });
  },

  async permanentlyDeleteService(id: string): Promise<void> {
    const docRef = doc(db, SERVICES_COLLECTION, id);
    await deleteDoc(docRef);
  },

  async importServices(services: Omit<Service, 'id' | 'createdAt'>[]): Promise<number> {
    const batchSize = 500;
    let imported = 0;

    for (let i = 0; i < services.length; i += batchSize) {
      const batch = writeBatch(db);
      const chunk = services.slice(i, i + batchSize);

      for (const service of chunk) {
        const docRef = doc(collection(db, SERVICES_COLLECTION));
        batch.set(docRef, {
          ...service,
          createdAt: Timestamp.now(),
          renewalDate: toTimestampOrNull(service.renewalDate),
          claimedDate: toTimestampOrNull(service.claimedDate),
          deletedDate: toTimestampOrNull(service.deletedDate),
        });
      }

      await batch.commit();
      imported += chunk.length;
    }

    return imported;
  },
};
